import { ObjectId } from 'mongodb';
import { ChatAccessFilter, IChatChannelInfo } from '../chat';

export namespace User {

    export interface User<IsJSON extends boolean = false> {
        _id: string;
        /**
         * @deprecated Use _id instead
         */
        uid: string;
        /**
         * User identifier in CRM
         */
        crmId?: string;
        metadata?: Metadata<IsJSON>;
        updatedAt?: IsJSON extends false ? Date : string;
        /** Agent contacts in different channels */
        contacts?: Contact[];
        color?: string;
        /**
         * The user's primary email, if set.
         */
        email?: string;
        /**
         * Whether or not the user's primary email is verified.
         */
        emailVerified: boolean;
        /**
         * The user's display name.
         */
        displayName?: string;
        /**
         * The user's primary phone number, if set.
         */
        phoneNumber?: string;
        /**
         * Whether or not the user is disabled: `true` for disabled; `false` for
         * enabled.
         */
        disabled?: boolean;
        /**
         * IPs only from which user allowed to login and access resources.
         * If not specified - then not restricted.
         */
        IPsAllowed?: string[];
        /**
         * User roles
         */
        roles: string[];
        /**
         * User access to particular chats.
         * Extends default access system allow rules.
         * This mean if access isn't granted by regular permissions,
         * it can be granted here. If granted by regular permissions,
         * then this rules not checked, and it can't be denied here.
         */
        chatsAccess?: ChatAccessFilter[];
        /**
         * Temporary user
         */
        temporary?: {
            /**
             * Access starting date
             */
            from?: IsJSON extends false ? Date : string;
            /**
             * Access ending date
             */
            till: IsJSON extends false ? Date : string;
        };
        favorites?: Favorites.Entry<IsJSON>[];
    }

    export namespace Favorites {

        export type Entry<IsJSON extends boolean = false> = {
            type: Type;
            id: IsJSON extends false ? ObjectId : string;
        };

        export enum Type {
            chat = 'chat',
            message = 'message',
        }

    }

    export enum Status {
        online = 'online',
        away = 'away',
        offline = 'offline',
    }

    export type Contact = Omit<IChatChannelInfo, 'metadata'>;

    /** Custom data that can be attached to specific users by/for external APIs */
    export type Metadata<IsJSON extends boolean = false> = {
        /**
         * The date the user was created.
         */
        creationTime: IsJSON extends false ? Date : string;
        /**
         * The date the user last signed in.
         */
        lastSignInTime: IsJSON extends false ? Date : string;
    } & {
        [key: string]: null | string | number | boolean | Array<string | number | boolean>;
    };

    export interface Session<IsJSON extends boolean = false> {
        _id: IsJSON extends false ? ObjectId : string;
        /**
         * IP address from which this session was created
         */
        ip: string;
        /**
         * Datetime when this session was created
         */
        created: IsJSON extends false ? Date : string;
        /**
         * Datetime when this session expires
         */
        expire: IsJSON extends false ? Date : string;
        /**
         * Client IPs allowed
         */
        IPsAllowed?: string[];
        /**
         * Session was started by recovery code
         */
        recovery?: true;
    }
}
